// 적 클래스 - 모든 적 타입의 공통 기능을 포함
class Enemy extends Entity {
  constructor(scene, x = 0, y = 0, z = 0, enemyType = "MINION") {
    // 적 타입에 따른 설정 가져오기
    const config = EnemyTypesConfig[enemyType] || EnemyTypesConfig.MINION;

    super(scene, x, y, z, config);

    // 적 타입 설정
    this.enemyType = enemyType;

    // Enemy 상태 설정 (Entity의 currentState 오버라이드)
    this.currentState = "chase"; // 'chase', 'attack', 'victory'

    // Enemy 전용 애니메이션: dance 애니메이션 (승리 시)
    this.danceAction = null;
  }

  // 적 초기화 (캐시 사용으로 최적화)
  async init() {
    try {
      // 캐시에서 모델 가져오기
      let gltf;
      if (window.enemyModelCache) {
        try {
          gltf = await window.enemyModelCache.createEnemyModel(this.enemyType);
        } catch (cacheError) {
          Utils.log(
            `${this.enemyType} 캐시 로드 실패, 직접 로드 시도: ${cacheError.message}`,
            "warn"
          );
          gltf = await this.loadModelDirectly();
        }
      } else {
        gltf = await this.loadModelDirectly();
        Utils.log(
          `${this.enemyType} 적이 성공적으로 소환되었습니다! (직접 로드)`
        );
      }

      this.model = gltf.scene;

      this.setupModelProperties();

      this.setupAnimations(gltf.animations);

      this.scene.add(this.model);

      // 씬에 추가한 후 위치 다시 설정 (Companion 방식과 동일하게)
      this.model.position.copy(this.position);

      this.isLoaded = true;
    } catch (error) {
      console.error(`${this.enemyType} 모델 로드 오류:`, error);
      throw error;
    }
  }

  // 직접 모델 로딩 (캐시 실패 시 백업)
  async loadModelDirectly() {
    return new Promise((resolve, reject) => {
      const loader = new THREE.GLTFLoader();

      loader.load(
        this.config.MODEL_PATH,
        (gltf) => {
          resolve({
            scene: gltf.scene,
            animations: gltf.animations,
          });
        },
        undefined,
        (error) => {
          console.error(`${this.enemyType} 직접 모델 로드 오류:`, error);
          reject(error);
        }
      );
    });
  }

  // 모델 속성 설정 오버라이드 (적 타입별 스케일 조정)
  setupModelProperties() {
    super.setupModelProperties();
    // 스케일은 이미 Entity에서 config.SCALE로 설정됨
  }

  // 애니메이션 인덱스 정의 (부모 클래스 메서드 오버라이드)
  getAnimationIndices() {
    return {
      idle: 42, // 대기 애니메이션
      move: 94, // 걷기 애니메이션
      aim: null, // 조준 애니메이션 없음
      attack: 4, // 공격 애니메이션
      hit: 38, // 피격 애니메이션
      death: 24, // 죽음 애니메이션
    };
  }

  // 애니메이션 설정 오버라이드 (부모 클래스 호출 + Enemy 전용 애니메이션 추가)
  setupAnimations(animations) {
    if (!animations || animations.length === 0) return;

    // 부모 클래스의 setupAnimations 호출 (공통 애니메이션 설정)
    super.setupAnimations(animations);

    // Enemy 전용: dance 애니메이션 설정 (승리 시)
    if (animations[23]) {
      this.danceAction = this.mixer.clipAction(animations[23]);
      this.danceAction.setLoop(THREE.LoopRepeat);
    }

    // allAnimations 배열에 dance 애니메이션 추가
    if (this.danceAction) {
      this.allAnimations.push(this.danceAction);
    }
  }

  // Enemy 업데이트 오버라이드 (AI 업데이트 포함)
  update(delta, playerPosition) {
    // 부모 클래스의 기본 업데이트 호출
    super.update(delta);

    // AI 업데이트 호출
    this.updateAI(delta, playerPosition);

    // 데미지 숫자 업데이트
    this.updateDamageNumbers(delta);
  }

  // AI 업데이트
  updateAI(delta, playerPosition) {
    // 강제 idle 상태면 AI 업데이트 하지 않음
    if (this.isForcedIdle) {
      this.playAnimation("idle");
      return;
    }

    // 죽고 있거나 죽은 상태면 AI 업데이트 하지 않음
    if (this.isHit || this.isDying || !this.isAlive) return;

    // Party 인원 중 가장 가까운 사람 찾기 (Player + Companion)
    const nearestPartyMember = this.findNearestPartyMember();

    if (nearestPartyMember) {
      // 가장 가까운 Party 멤버를 타겟팅
      const distanceToTarget = this.position.distanceTo(
        nearestPartyMember.position
      );

      // 공격 범위 체크 - config의 ATTACK_RANGE 사용
      if (distanceToTarget <= this.attackRange) {
        this.currentState = "attack";
        this.attackPartyMember(delta, nearestPartyMember);
      } else {
        // 공격 범위 밖이면 추적
        this.currentState = "chase";
        this.chasePartyMember(delta, nearestPartyMember);
      }
    } else {
      // Party 멤버가 모두 죽었으면 승리 애니메이션
      this.currentState = "victory";
      this.playVictoryAnimation();
    }
  }

  // Party 인원 중 가장 가까운 사람 찾기 (Player + Companion)
  findNearestPartyMember() {
    let nearestPartyMember = null;
    let nearestDistance = Infinity;

    // Player 확인
    const player = window.game?.player;
    if (player && player.isAlive) {
      const distanceToPlayer = this.position.distanceTo(player.position);
      if (distanceToPlayer < nearestDistance) {
        nearestDistance = distanceToPlayer;
        nearestPartyMember = player;
      }
    }

    // Companion들 확인
    const companionManager = window.game?.companionManager;
    if (companionManager && companionManager.companions) {
      companionManager.companions.forEach((companion) => {
        if (companion.isAlive) {
          const distance = this.position.distanceTo(companion.position);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestPartyMember = companion;
          }
        }
      });
    }

    return nearestPartyMember;
  }

  // Party 멤버 추적 (Player 또는 Companion)
  chasePartyMember(delta, partyMember) {
    // 죽고 있거나 죽은 상태면 추적하지 않음
    if (this.isDying || !this.isAlive) return;

    const direction = new THREE.Vector3()
      .copy(partyMember.position)
      .sub(this.position)
      .normalize();

    this.moveInDirection(direction, delta);
    this.playAnimation("walk");
  }

  // Party 멤버 공격 (Player 또는 Companion)
  attackPartyMember(delta, partyMember) {
    // 죽고 있거나 죽은 상태면 공격하지 않음
    if (this.isDying || !this.isAlive) return;

    const currentTime = Date.now() / 1000;

    // 쿨타임 체크 - 쿨타임이 지나야만 공격 가능
    if (currentTime - this.lastAttackTime >= this.attackCooltime) {
      // 공격 상태로 전환
      this.isAttacking = true;

      // Party 멤버 방향을 바라보기
      const direction = new THREE.Vector3()
        .copy(partyMember.position)
        .sub(this.position)
        .normalize();
      this.lookAtDirection(direction);

      // 공격 애니메이션 재생
      this.playAnimation("attack");

      // 한국어 주석: 공격 애니메이션 duration의 50% 지점에 데미지 적용 (즉시 데미지 제거)
      const attackDelay = this.attackAction
        ? this.attackAction.getClip().duration * 0.5
        : this.attackCooltime * 0.5;

      // 데미지 적용을 지연시킴 (즉시 데미지 제거)
      const attackDamage = this.attackDamage;
      const attackerPos = this.position.clone();

      setTimeout(() => {
        // 공격 시점에 타겟의 생존 여부와 데미지 받을 수 있는 상태 확인
        // 한국어 주석: 방어 로직 - 데미지 적용 직전에 실제 거리가 공격 범위 내인지 재검증
        // 이동으로 인해 공격 타이밍에 범위를 벗어났다면 데미지를 적용하지 않음
        const currentDistance = this.position.distanceTo(partyMember.position);
        const isWithinRange = currentDistance <= this.attackRange;

        if (partyMember.isAlive && partyMember.canTakeDamage() && isWithinRange) {
          partyMember.takeDamage(attackDamage, attackerPos);
        }
      }, attackDelay * 1000);

      // 공격 시간 업데이트 (애니메이션 시작 시점에 쿨타임 시작)
      this.lastAttackTime = currentTime;
    } else {
      // 쿨타임 중에는 idle 애니메이션 재생하고 타겟을 바라보기만 함
      const direction = new THREE.Vector3()
        .copy(partyMember.position)
        .sub(this.position)
        .normalize();
      this.lookAtDirection(direction);

      // 공격 애니메이션이 아니라면 idle 재생
      if (this.currentAnimation !== this.attackAction) {
        this.playAnimation("idle");
      }
    }
  }

  // 방향으로 이동
  moveInDirection(direction, delta) {
    // 죽고 있거나 죽은 상태면 이동하지 않음
    if (this.isDying || !this.isAlive) return;

    const moveVector = direction.clone().multiplyScalar(this.moveSpeed);
    const newPosition = new THREE.Vector3().copy(this.position).add(moveVector);

    // 환경 충돌 검사
    this.model.position.copy(newPosition);
    this.position.copy(newPosition);
    this.lookAtDirection(direction);
  }

  // 방향으로 회전
  lookAtDirection(direction) {
    if (!this.model || direction.length() === 0) return;

    // 죽고 있거나 죽은 상태면 회전하지 않음
    if (this.isDying || !this.isAlive) return;

    const targetRotation = Math.atan2(direction.x, direction.z);

    // 부드러운 회전
    let rotationDifference = targetRotation - this.model.rotation.y;
    while (rotationDifference > Math.PI) rotationDifference -= 2 * Math.PI;
    while (rotationDifference < -Math.PI) rotationDifference += 2 * Math.PI;

    this.model.rotation.y += rotationDifference * 0.1;
  }

  // 죽음 애니메이션 재생 오버라이드
  playDeathAnimation() {
    // 부모 클래스의 playDeathAnimation 호출 (공통 죽음 애니메이션 재생)
    super.playDeathAnimation();

    // 데미지 숫자들 정리
    this.clearDamageNumbers();

    // 다이아몬드 드롭 (실제로 죽고 있는 경우에만)
    if (this.isDying && !this.isAlive) {
      this.dropDiamond();
    }
  }

  // 다이아몬드 드롭
  async dropDiamond() {
    // 실제로 죽고 있는 상태가 아니면 아이템 드롭하지 않음 (보스 스폰 시 버그 방지)
    if (!this.isDying || this.isAlive) {
      return;
    }

    try {
      // 보스가 죽을 때 실제 위치를 사용 (모델 위치 대신 this.position 사용)
      // 죽음 애니메이션 중 모델 위치가 변경될 수 있으므로 안전하게 this.position 사용
      const dropPosition = this.position;

      // Config에서 다이아몬드 타입 가져오기
      const diamondType = this.config.DIAMOND_TYPE || "DIAMOND_BLUE";

      const diamond = new Diamond(
        this.scene,
        dropPosition.x,
        dropPosition.z,
        diamondType
      );
      await diamond.init();

      // ItemManager에 추가
      if (window.game && window.game.itemManager) {
        window.game.itemManager.items.push(diamond);
      }

      // 보스가 죽으면 특별 아이템들 드롭 (자석, 폭탄, 하트)
      if (this.enemyType === "WARRIOR") {
        await this.dropBossItems(dropPosition);
      }
    } catch (error) {
      console.error(`다이아몬드 드롭 실패: ${error.message}`);
    }
  }

  // 보스 전용 아이템 드롭 (자석, 폭탄, 하트)
  async dropBossItems(dropPosition) {
    try {
      const itemManager = window.game?.itemManager;
      if (!itemManager) return;

      // 자석 드롭
      const magnet = new Magnet(this.scene, dropPosition.x - 1, dropPosition.z);
      await magnet.init();
      magnet.setItemManager(itemManager);
      itemManager.items.push(magnet);

      // 폭탄 드롭
      const bomb = new Bomb(this.scene, dropPosition.x + 1, dropPosition.z);
      await bomb.init();
      itemManager.items.push(bomb);

      // 하트 드롭
      const heart = new Heart(this.scene, dropPosition.x, dropPosition.z - 1);
      await heart.init();
      itemManager.items.push(heart);

      // 별 드롭
      const star = new Star(this.scene, dropPosition.x, dropPosition.z + 1);
      await star.init();
      itemManager.items.push(star);
    } catch (error) {
      console.error(`보스 아이템 드롭 실패: ${error.message}`);
    }
  }

  // 데미지 받기 오버라이드 (킬 카운트 추가)
  takeDamage(damage, attackerPosition = null) {
    // 데미지를 받기 전에 데미지 숫자 표시 (isAlive 체크 전에)
    if (this.model && this.health > 0) {
      this.showDamageNumber(damage);
    }

    const result = super.takeDamage(damage, attackerPosition);

    if (result) {
      // 죽음
      // 플레이어에게 킬 카운트 전달
      const player = window.game?.player;
      if (player) {
        player.addKill(this.enemyType);
      }
    }

    return result;
  }

  // 승리 애니메이션 재생 (dance)
  playVictoryAnimation() {
    if (!this.danceAction || !this.isAlive || this.isDying) return;

    this.isForcedIdle = false;
    this.switchAnimation(this.currentAnimation, this.danceAction, 0.4);
    this.currentState = "victory";
  }

  // 애니메이션 상태 완전 초기화 (게임 재시작용) - 부모 클래스 메서드 오버라이드
  resetAnimationState() {
    // 부모 클래스 메서드 호출
    super.resetAnimationState();

    // Enemy 전용 상태 초기화
    this.isForcedIdle = false;
    this.currentState = "chase";
  }
}

// 전역으로 내보내기
if (typeof module !== "undefined" && module.exports) {
  module.exports = Enemy;
} else {
  window.Enemy = Enemy;
}

// webpack 빌드를 위해 default export 추가
export default Enemy;
